import * as React from 'react';
import { useState, useEffect, Fragment, useRef } from 'react';

/** Material UI Packages. */
import { Box, Typography, ToggleButton } from '@mui/material';

/** Material UI Icons. */
import AddIcon from '@mui/icons-material/Add';
import HistoryIcon from '@mui/icons-material/History';

/** Redux Packages. */
import { useSelector, useDispatch } from 'react-redux';

/** Redux Reducers. */
import { addStatusEditor, setStatusEditorData } from 'shared/store/slices/statusEditorSlice';

/** Date & Time converting library. */
import dayjs from 'dayjs';

/** Internal Components. */
import ThemeButton from 'shared/ui/Button';
import { CustomHistoryPopover } from 'shared/ui/Popover';
import useUserRole from 'shared/hooks/useUserRole';
import { readSocketData } from 'shared/lib/fetchSocket';
import { getOperationToday } from 'shared/lib/dateFormatter';

import { renderSwitch, renderStatusForm } from './StatusForm';
import { StatusRenderer } from './StatusRenderer';
import { createStatusHandlers } from './StatusHandler';

/** White card look of the detail panel's SectionBox, reused for the drawer's two sections. */
const DRAWER_CARD_SX = {
    bgcolor: 'white',
    borderRadius: '10px',
    boxShadow: '0px 1px 2px 1px rgba(0, 0, 0, 0.1)',
};

const UpdateStatus = (props) => {
    /** Get props from ShipmentDetail.js (or StatusDrawer.js when rendered inside the drawer). */
    const { row, statusConfig, hblTabValue, variant = 'panel' } = props;
    
    const isDrawer = variant === 'drawer';

    /** Get user role from useUserRole.js. */
    const userRole = useUserRole();

    /** Get the dispatch function from Redux to send actions to the store. */
    const dispatch = useDispatch();

    /** Shipment type data. */
    const tabValue = useSelector((state) => state.tabValueForm);
    /** Status list for each shipment type. */
    const statusList = useSelector((state) => state[statusConfig.statusData]);

    /** Matching HBL shipment list based on the main data's ID value. */
    const hblShipmentList = useSelector((state) => {
        const list = state[statusConfig.hblShipmentList];

        if (!Array.isArray(list)) return [];

        return list
            .filter((item) => item[statusConfig.mainId] === row.id)
            .slice()
            .sort((a, b) => a.id - b.id);
    });

    /** Selected house row. */
    let selectedHouseRow = statusConfig.type === 'trucking' ? true : hblShipmentList[hblTabValue];

    /** Container data for each shipment type. (If air, it will be Alias Data.) */
    const cntrShipmentList = useSelector((state) => state[statusConfig.cntrShipmentList]);
    /** Split data for each shipment type. */
    const aliasShipmentList = useSelector((state) => state[statusConfig.aliasShipmentList]);
    /** Trucking Alias data. */
    const bolShipmentList = useSelector((state) => state.truckingBolUpdateForm);
    const statusEditor = useSelector((state) => state.statusEditor);

    /** Initial status data. */
    const initialStatus = () => ({
        shipment_type: tabValue,
        // 한국에서 폼을 열어도 LA 담당자와 같은 업무일이 기본으로 잡히게 한다.
        event_date: dayjs(getOperationToday()),
        parent_id: row.id,
        house_id: '',
        child_id: [],
        alias_id: '',
        status_message_id: '',
        associate_textfield: '',
        associate_date: null,
        associate_time: null,
        associate_location: {},
        hidden: false,
        afternoon: null,
    });

    /** Current status input value. */
    const [status, setStatus] = useState(initialStatus());
    /** Status message list for the corresponding shipment.  */
    const [statusMsg, setStatusMsg] = useState([]);
    /** Container number list for the corresponding shipment. */
    const [cntrList, setCntrList] = useState([]);
    /** Alias name list for the corresponding shipment. */
    const [aliasList, setAliasList] = useState([]);
    /** State to manage location list received via WebSocket (ocean: port meta, air: airport meta). */
    const [locationList, setLocationList] = useState([]);
    /** State to manage whether there are any splits for OIM */
    const [hasAlias, setHasAlias] = useState(false);
    /** State to manage whether it is currently being edited. */
    const [isEdit, setIsEdit] = useState(false);
    /** List of status messages received via WebSocket. */
    const [statusOptions, setStatusOptions] = useState([]);
    /** State to manage the associate values of the currently selected status message. */
    const [associateValue, setAssociateValue] = useState('');
    /** State to prevent time input when the checkbox(afternoon) is selected. */
    const [disableTime, setDisableTime] = useState(false);
    /** State to manage the hidden list. */
    const [show, setShow] = useState(false);

    // /** State for the status history of the selected shipment received from the server. */
    const [historyList, setHistoryList] = useState([]);
    // /** Current history index. */
    const [indexRef, setIndexRef] = useState(0);
    /** Id of the oldest history entry loaded so far — the cursor for the next 'Prev' fetch. */
    const cursorRef = useRef(null);
    /** Whether an older history entry still exists on the server (drives the 'Prev' button). */
    const [hasPrev, setHasPrev] = useState(false);
    /** Current editor data. */
    const [currentEditor, setCurrentEditor] = useState({});
    /** State to display the position of the memo popover. */
    const [historyAnchorEl, setHistoryAnchorEl] = useState(null);
    /** Check if the popover is open. */
    const historyOpen = Boolean(historyAnchorEl);

    /** Check if case_closure is Complete */
    const isCaseClosureComplete = row?.case_closure === 'Complete';

    /** Check if the selected message is a warning. */
    let findWarningStatus =
        statusList.length > 0
            ? statusMsg.filter((item) => item.warning === true).map((item) => item.status_message_id)
            : '';

    const {
        handleEventDateChange,
        handleDateChange,
        handleDateTimeChange,
        handleRelatedDataChange,
        handleAliasChange,
        handleStatusChange,
        handleStatusListChange,
        handleLocationListChange,
        handleLocationChange,
        handleTextChange,
        handleAfternoonChange,
        handleShowChange,
        handleResetStatus,
        handleHistoryClick,
        handleHistoryClose,
        handleAddStatus,
        handleUpdateStatus,
        handleDeleteStatus,
        handleHiddenStatus,
        handleEditStatus,
        handleAllSelectedChange,
    } = createStatusHandlers({
        status,
        setStatus,
        setHasAlias,
        setStatusOptions,
        setAssociateValue,
        setIsEdit,
        setShow,
        setHistoryList,
        setIndexRef,
        setHistoryAnchorEl,
        setLocationList,
        cursorRef,
        setHasPrev,
        row,
        tabValue,
        statusConfig,
        selectedHouseRow,
        statusList,
        dispatch,
        initialStatus,
        isEdit,
    });

    /** Hook to request the last modified by data when the component is mounted. */
    useEffect(() => {
        const findStatusEditor = statusEditor.find((item) => item.parent_id === row.id);
        if (!findStatusEditor) {
            readSocketData(dispatch, tabValue, 'row', 'il_status_editor', '', { parent_id: row.id }, (receivedData) => {
                if (receivedData.length > 0) {
                    dispatch(addStatusEditor([receivedData]));
                } else {
                    dispatch(setStatusEditorData([receivedData]));
                }
            });
        }
    }, []);

    /** Hook to update the current editor data. */
    useEffect(() => {
        const findStatusEditor = statusEditor.find((item) => item.parent_id === row.id);
        setCurrentEditor(findStatusEditor);
    }, [statusEditor]);

    /** Hook to update the container list. */
    useEffect(() => {
        // Create a list of containers or aliases including "Warning Sign".
        const setContainerList = (matchedList) => {
            const isAir =
                statusConfig.type === 'aim' || statusConfig.type === 'aex' || statusConfig.type === 'trucking';
            const cntrList = matchedList.map((item) => ({
                id: item.id,
                name: isAir ? (item.alias ?? item.cntr_number ?? '') : (item.cntr_number ?? ''),
            }));
            setCntrList((prev) => [{ id: 0, name: 'Warning Sign' }, ...cntrList]);
        };

        if (statusConfig.type === 'trucking') {
            // Find the matched BOL list for select list.
            const matchedBolList = bolShipmentList.filter((item) => item.il_ftl_ltl_id === row.id);
            setContainerList(matchedBolList);
        } else {
            if (!selectedHouseRow) {
                setContainerList([]);
                return;
            }

            // Find the matched container list for select list.
            const matchedCntrList = cntrShipmentList.filter(
                (item) =>
                    selectedHouseRow[statusConfig.mainId] === row.id &&
                    selectedHouseRow?.id === item[statusConfig.hblId],
            );
            setContainerList(matchedCntrList);
        }
        // Filter and sort the status list based on the date, id.
        const filtered_status = statusList
            ?.filter((item) => (show ? true : item.hidden === false))
            .filter((item) => {
                const parentMatch = item.parent_id === row.id;
                const houseMatch = statusConfig.type === 'trucking' ? true : item.house_id === selectedHouseRow?.id;
                return parentMatch && houseMatch;
            })
            .sort((a, b) => {
                if (a.child_id !== b.child_id) {
                    return a.child_id - b.child_id;
                }

                const dateA = new Date(a.event_date).toISOString().split('T')[0];
                const dateB = new Date(b.event_date).toISOString().split('T')[0];

                if (dateA !== dateB) {
                    return dateA.localeCompare(dateB);
                }
                return a.id - b.id;
            });
        setStatusMsg(filtered_status);
    }, [statusList, cntrShipmentList, bolShipmentList, show, selectedHouseRow]);

    useEffect(() => {
        if (!aliasShipmentList) return;

        if (isEdit) {
            // Edit: child_id는 단일 id (number)
            const childId = status.child_id;
            const aliasData = aliasShipmentList
                .filter((item) => item[statusConfig.cntrId] === childId && item.alias_name.length > 0)
                .map(({ id, alias_name }) => ({ id, content: alias_name }));
            setAliasList(aliasData);
            setHasAlias(aliasData.length > 0);
            return;
        }

        // Add: child_id는 객체 배열
        const childIdArr = status.child_id;
        if (!Array.isArray(childIdArr) || childIdArr.length >= 2) {
            setAliasList([]);
            setHasAlias(false);
            return;
        }
        if (childIdArr.length === 0) {
            setAliasList([]);
            setHasAlias(false);
            return;
        }
        const childId = typeof childIdArr[0] === 'object' && childIdArr[0] !== null ? childIdArr[0].id : childIdArr[0];
        const aliasData = aliasShipmentList
            .filter((item) => item[statusConfig.cntrId] === childId && item.alias_name.length > 0)
            .map(({ id, alias_name }) => ({ id, content: alias_name }));
        setAliasList(aliasData);
        setHasAlias(aliasData.length > 0);
    }, [status.child_id, aliasShipmentList, statusConfig.cntrId, isEdit]);

    /** Hook that updates the state to disable the timeField when 'afternoon' is set. */
    useEffect(() => {
        setDisableTime(status.afternoon != null);

        if (status.afternoon != null) {
            setStatus((prev) => ({ ...prev, associate_time: null }));
        }
    }, [status.afternoon]);

    /** Renders additional fields dynamically based on the associateValue (array or string). */
    const renderAssociateFields = () => {
        const values = Array.isArray(associateValue) ? associateValue : [associateValue];

        return values.map((value, index) => (
            <Fragment key={index}>
                {renderSwitch(
                    value,
                    status,
                    handleDateChange,
                    handleDateTimeChange,
                    handleTextChange,
                    handleAfternoonChange,
                    setStatus,
                    locationList,
                    handleLocationChange,
                    handleLocationListChange,
                    tabValue,
                    disableTime,
                    isCaseClosureComplete,
                )}
            </Fragment>
        ));
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
                width: '100%',
                ...(isDrawer && { height: '100%', p: '10px', gap: '10px', bgcolor: '#f8f8f8' }),
            }}
        >
            <Box
                sx={
                    isDrawer
                        ? { flex: 1, minHeight: 0, p: '10px', overflow: 'auto', ...DRAWER_CARD_SX }
                        : {
                              height: statusConfig.listHeight,
                              p: 1,
                              overflow: 'scroll',
                              minHeight: '250px',
                              maxHeight: '430px',
                          }
                }
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: isDrawer ? 11 : 10, color: '#003a52' }}>
                        <strong>{selectedHouseRow?.hbl ?? selectedHouseRow?.hawb ?? ''}</strong> / Edit by:{' '}
                        {currentEditor?.editor}
                    </Typography>
                    <ToggleButton
                        value={show}
                        selected={show}
                        onClick={handleShowChange}
                        sx={{
                            width: isDrawer ? '76px' : '60px',
                            ...(isDrawer && { height: '24px', py: 0, whiteSpace: 'nowrap', flexShrink: 0 }),
                            fontSize: isDrawer ? 11 : 10,
                            fontWeight: 'bold',
                            color: '#003a52',
                            bgcolor: 'white',
                            border: '1px solid #003a52',
                            borderRadius: 2,
                            '&.Mui-selected': {
                                bgcolor: '#003a52',
                                color: 'white',
                            },
                        }}
                    >
                        SHOW ALL
                    </ToggleButton>
                </Box>
                <StatusRenderer
                    statusMsg={statusMsg}
                    cntrList={cntrList}
                    aliasShipmentList={aliasShipmentList}
                    statusConfig={statusConfig}
                    findWarningStatus={findWarningStatus}
                    onEditStatus={handleEditStatus}
                    onHiddenStatus={handleHiddenStatus}
                    onDeleteStatus={handleDeleteStatus}
                    isCaseClosureComplete={isCaseClosureComplete}
                    variant={variant}
                />
            </Box>
            <Box
                sx={
                    isDrawer
                        ? { flexShrink: 0, ...DRAWER_CARD_SX }
                        : { height: statusConfig.inputHeight }
                }
            >
                <Typography
                    sx={{
                        color: '#003a52',
                        fontWeight: 500,
                        bgcolor: '#f1f1f1',
                        fontSize: isDrawer ? '14px' : '13px',
                        p: '10px',
                        borderTopLeftRadius: '10px',
                        borderTopRightRadius: '10px',
                    }}
                >
                    {isEdit ? 'Edit Status' : 'New Status'}

                    <CustomHistoryPopover
                        historyList={historyList}
                        setHistoryList={setHistoryList}
                        indexRef={indexRef}
                        setIndexRef={setIndexRef}
                        cursorRef={cursorRef}
                        hasPrev={hasPrev}
                        setHasPrev={setHasPrev}
                        open={historyOpen}
                        anchorEl={historyAnchorEl}
                        handleClose={handleHistoryClose}
                        handleClick={handleHistoryClick}
                        icon={<HistoryIcon />}
                        rows={row}
                        selectedHouseRow={selectedHouseRow}
                    />
                </Typography>
                <Box
                    sx={{
                        p: isDrawer ? '10px' : '11px',
                        '&& [aria-label="Open calendar"]': { pl: '5px' },
                        ...(!isDrawer && {
                            '&& .MuiTextField-root:has(+ [aria-label="Open calendar"])': {
                                width: 'calc(100% - 5px)',
                            },
                        }),
                        ...(isDrawer && {
                            // Field labels only (checkbox captions are Typography too, excluded below).
                            // Label-to-field spacing: StatusForm rows use gap 8px on top of this margin.
                            '&& .MuiTypography-root:not(.MuiFormControlLabel-label)': {
                                fontSize: '13px',
                                flexShrink: 0,
                                mr: '110px',
                            },
                            // Checkbox groups ("Select all", "Before noon"/"Afternoon") start under the inputs:
                            // label 100px + row gap 8px + label mr 110px. Kept on one row so the second box never wraps.
                            '&& .MuiFormGroup-root': { ml: '218px', display: 'flex', flexDirection: 'row', flexWrap: 'nowrap' },
                            '&& .MuiFormControlLabel-root': { gap: '0px', mb: '5px' },
                            '&& .MuiCheckbox-root': { p: '2px', mr: '8px' },
                            '&& .MuiFormControlLabel-label': { fontSize: '12px', pl: 0 },
                            '&& .MuiInputBase-input': { fontSize: '14px', height: '16px' },
                            '&& .MuiAutocomplete-root': { height: 'auto' },
                            '&& .MuiOutlinedInput-root:not(.MuiAutocomplete-inputRoot)': { height: '31px' },
                            '&& .MuiOutlinedInput-root': { width: '100%' },
                            '&& .MuiChip-label': { fontSize: '12px' },
                        }),
                    }}
                >
                    {renderStatusForm({
                        status,
                        statusConfig,
                        hasAlias,
                        cntrList,
                        aliasList,
                        statusOptions,
                        userRole,
                        handleEventDateChange,
                        handleRelatedDataChange,
                        handleAliasChange,
                        handleStatusChange,
                        handleStatusListChange,
                        handleAllSelectedChange,
                        renderAssociateFields,
                        associateValue,
                        isCaseClosureComplete,
                        isEdit,
                    })}

                    <Box sx={{ textAlign: 'end', mt: 1, ...(isDrawer && { pb: '2px' }) }}>
                        <ThemeButton
                            text={isEdit ? 'CANCEL' : 'CLEAR'}
                            width={'68px'}
                            onClick={handleResetStatus}
                            disabled={isCaseClosureComplete}
                            sx={{ mr: 1, border: '#00647e', bgcolor: 'white', color: '#00647e' }}
                        />
                        <ThemeButton
                            text={isEdit ? 'UPDATE' : 'ADD'}
                            icon={isEdit ? '' : <AddIcon />}
                            width={'68px'}
                            onClick={isEdit ? handleUpdateStatus : handleAddStatus}
                            disabled={isCaseClosureComplete}
                        />
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};
export default React.memo(UpdateStatus);
